import React from 'react';
import { Link } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { useTeamSchedules } from '../../hooks/useSchedule';
import { Schedule, ScheduleStatus } from '../../types/schedule';
import Button from '../common/Button';
import ScheduleStatusBadge from './ScheduleStatusBadge';
import Spinner from '../common/Spinner';

interface ScheduleListProps {
  teamId: string;
}

const ScheduleList: React.FC<ScheduleListProps> = ({ teamId }) => {
  const { schedules, isLoading, error: scheduleError } = useTeamSchedules(teamId);

  if (isLoading) {
    return <Spinner />;
  }

  if (scheduleError) {
    return (
      <div className="text-center py-8 text-red-500">
        Error loading schedules. Please try again.
      </div>
    );
  }

  if (!schedules || schedules.length === 0) {
    return (
      <div className="text-center py-12 border rounded-lg bg-gray-50">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Schedules Yet</h3>
        <p className="text-gray-500 mb-6">
          Create a new schedule to start planning your team's shifts.
        </p>
        <Link to={`/teams/${teamId}/schedules/new`}>
          <Button variant="primary">Create Schedule</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Period
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Preferences Due
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {schedules.map((schedule) => (
            <ScheduleRow key={schedule.id} schedule={schedule} />
          ))}
        </tbody>
      </table>
    </div>
  );
};

interface ScheduleRowProps {
  schedule: Schedule;
}

const ScheduleRow: React.FC<ScheduleRowProps> = ({ schedule }) => {
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy');
    } catch (formatError) {
      console.error('Error formatting date:', formatError);
      return dateString;
    }
  };

  return (
    <tr>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="font-medium text-gray-900">
          {formatDate(schedule.start_date)} - {formatDate(schedule.end_date)}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <ScheduleStatusBadge status={schedule.status as ScheduleStatus} />
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        {schedule.preferences_due_date ? (
          <span className="text-gray-500">
            {formatDate(schedule.preferences_due_date)}
          </span>
        ) : (
          <span className="text-gray-400">Not set</span>
        )}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-gray-500">
        {formatDate(schedule.created_at)}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <Link
          to={`/teams/${schedule.team_id}/schedules/${schedule.id}`}
          className="text-primary hover:text-primary-dark font-medium"
        >
          View
        </Link>
      </td>
    </tr>
  );
};

export default ScheduleList; 